var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _Jolt_instances, _Jolt_pingTimeout, _Jolt_pingSetTimeout, _Jolt_pong, _Jolt_handlePingTimeout, _Jolt_cleanupAndCloseWebsocket, _Jolt_isWebSocketClosingOrClosed, _Jolt_reestablishConnection, _Jolt_resubscribeToAllSubscriptions, _Jolt_initializeWebSocket, _Jolt_fetchAuthToken;
const DEFAULT_RETRY_TIMEOUT = 3000;
export default class Jolt {
    constructor(wssEndpoint, { authToken, fetchAuthTokenFn, fetchSubscribeTokenFn }, { logToConsole = false, pingInterval = 240000, pingTimeout = 2000, retryTimeout = () => DEFAULT_RETRY_TIMEOUT, } = {}) {
        this.wssEndpoint = wssEndpoint;
        _Jolt_instances.add(this);
        _Jolt_pingTimeout.set(this, void 0);
        _Jolt_pingSetTimeout.set(this, void 0);
        if ((typeof authToken !== "string" || authToken === "") &&
            typeof fetchAuthTokenFn !== "function") {
            throw new Error("Neither authToken nor fetchAuthTokenFn are supplied");
        }
        // We know one of these must have been non-blank from the check above this.
        this.fetchAuthTokenFn =
            typeof fetchAuthTokenFn === "function"
                ? fetchAuthTokenFn
                : async () => authToken;
        this.fetchSubscribeTokenFn = fetchSubscribeTokenFn;
        this._logToConsole = logToConsole;
        this._pingInterval = pingInterval;
        this._retryTimeout = retryTimeout;
        __classPrivateFieldSet(this, _Jolt_pingTimeout, pingTimeout, "f");
        this.subscriptionsInProgress = [];
        this.subscriptions = [];
        this._intentionallyDisconnected = false;
        this._resubscribeOnReconnect = false;
        this.connection = new Promise((resolve, reject) => this._connect(resolve, reject));
    }
    subscribe(channel, { fetchSubscribeTokenFn, claims = {} } = {}) {
        const channelSubscription = new ChannelSubscription(channel, this, fetchSubscribeTokenFn, { ...claims });
        this._log(`Subscription to ${channelSubscription.channel} is in progress`);
        this.subscriptionsInProgress.push(channelSubscription);
        channelSubscription._initiate();
        return channelSubscription;
    }
    unsubscribe(channel) {
        const channelSubscription = this.subscriptions.find((subscription) => subscription.channel === channel);
        if (channelSubscription) {
            channelSubscription._unsubscribe();
        }
    }
    disconnect() {
        var _a;
        this._log("Disconnecting");
        this._intentionallyDisconnected = true;
        (_a = this._webSocket) === null || _a === void 0 ? void 0 : _a.close();
    }
    ping() {
        this._webSocket.send(JSON.stringify({ event: "jolt.ping" }));
        __classPrivateFieldSet(this, _Jolt_pingSetTimeout, setTimeout(() => {
            __classPrivateFieldGet(this, _Jolt_instances, "m", _Jolt_handlePingTimeout).call(this);
        }, __classPrivateFieldGet(this, _Jolt_pingTimeout, "f")), "f");
    }
    async reviveConnection() {
        this._log("Attempting to revive connection");
        await __classPrivateFieldGet(this, _Jolt_instances, "m", _Jolt_cleanupAndCloseWebsocket).call(this);
        await __classPrivateFieldGet(this, _Jolt_instances, "m", _Jolt_reestablishConnection).call(this);
        await __classPrivateFieldGet(this, _Jolt_instances, "m", _Jolt_resubscribeToAllSubscriptions).call(this);
    }
    async _connect(resolveConnection, rejectConnection, previousTimeout = null, retryCount = 0) {
        this._log("Connecting...");
        const authToken = await __classPrivateFieldGet(this, _Jolt_instances, "m", _Jolt_fetchAuthToken).call(this, previousTimeout, retryCount);
        if (this._webSocket) {
            // Chrome is known to resurrect closed websockets, and we don't want that. So, um, *extra* close it!
            this._log("Closing existing WebSocket connection");
            this._webSocket.close();
        }
        await __classPrivateFieldGet(this, _Jolt_instances, "m", _Jolt_initializeWebSocket).call(this, {
            resolveConnection,
            rejectConnection,
            authToken,
        });
    }
    _removeSubscription(channel) {
        this._log(`Unsubscribed from ${channel}`);
        this.subscriptions = this.subscriptions.filter((subscription) => {
            if (subscription.channel === channel) {
                subscription._onUnsubscribed();
                return false;
            }
            return true;
        });
    }
    _handleMessage({ channel, event, data }) {
        const channelSubscriptions = this.subscriptions.filter((subscription) => subscription.channel === channel);
        const eventBindings = channelSubscriptions.reduce((bindings, subscription) => bindings.concat(subscription.bindings.filter((binding) => (binding.event === event || binding.event === "") &&
            typeof binding.callback === "function")), []);
        eventBindings.forEach((binding) => this._asynchronouslyInvokeCallback(binding.callback, {
            channel,
            event,
            data,
        }));
    }
    async _asynchronouslyInvokeCallback(callback, args) {
        callback(args);
    }
    _reconnectAndSubscribeUntilSuccessful(resolve, reject, previousTimeout, retryCount) {
        this._log("Attempting reconnect and resubscribe");
        this._resubscribeOnReconnect = true;
        this._connect(resolve, reject, previousTimeout, retryCount);
    }
    _failChannelSubscriptionInProgress(channel, message) {
        this._log(`Unable to subscribe to ${channel}: ${message}`);
        this.subscriptionsInProgress = this.subscriptionsInProgress.filter((subscription) => {
            if (subscription.channel === channel) {
                subscription._onUnsubscribed();
                return false;
            }
            return true;
        });
    }
    _succeedChannelSubscriptionInProgress(channel) {
        this._log(`Subscription to ${channel} succeeded`);
        const successfulSubscriptions = this.subscriptionsInProgress.filter((subscription) => subscription.channel === channel);
        this.subscriptionsInProgress = this.subscriptionsInProgress.filter((subscription) => subscription.channel !== channel);
        successfulSubscriptions.forEach((subscription) => subscription._onSubscribed());
        this.subscriptions = this.subscriptions.concat(successfulSubscriptions);
    }
    _log(message, ...args) {
        this._logToConsole && console.log(`[Jolt] ${message}`, ...args);
    }
}
_Jolt_pingTimeout = new WeakMap(), _Jolt_pingSetTimeout = new WeakMap(), _Jolt_instances = new WeakSet(), _Jolt_pong = function _Jolt_pong() {
    __classPrivateFieldGet(this, _Jolt_pingSetTimeout, "f") && clearTimeout(__classPrivateFieldGet(this, _Jolt_pingSetTimeout, "f"));
}, _Jolt_handlePingTimeout = function _Jolt_handlePingTimeout() {
    this.reviveConnection();
}, _Jolt_cleanupAndCloseWebsocket = function _Jolt_cleanupAndCloseWebsocket() {
    return new Promise((resolve) => {
        if (__classPrivateFieldGet(this, _Jolt_instances, "m", _Jolt_isWebSocketClosingOrClosed).call(this)) {
            return resolve();
        }
        const timeout = setTimeout(() => {
            resolve();
        }, 500);
        this._webSocket.onclose = () => {
            clearTimeout(timeout);
            resolve();
        };
        this._webSocket.onerror = () => undefined;
        this._webSocket.onmessage = () => undefined;
        this._webSocket.onopen = () => undefined;
        this._webSocket.close();
    });
}, _Jolt_isWebSocketClosingOrClosed = function _Jolt_isWebSocketClosingOrClosed() {
    return (this._webSocket.readyState === WebSocket.CLOSING ||
        this._webSocket.readyState === WebSocket.CLOSED);
}, _Jolt_reestablishConnection = async function _Jolt_reestablishConnection() {
    const authToken = await __classPrivateFieldGet(this, _Jolt_instances, "m", _Jolt_fetchAuthToken).call(this);
    return await (this.connection = new Promise((resolve, reject) => {
        __classPrivateFieldGet(this, _Jolt_instances, "m", _Jolt_initializeWebSocket).call(this, {
            resolveConnection: resolve,
            rejectConnection: reject,
            authToken,
        });
    }));
}, _Jolt_resubscribeToAllSubscriptions = async function _Jolt_resubscribeToAllSubscriptions() {
    this._log("resubscribing to:", this.subscriptions.map((sub) => sub.channel));
    this._resubscribeOnReconnect = false;
    this.subscriptionsInProgress = [
        ...this.subscriptionsInProgress,
        ...this.subscriptions,
    ];
    this.subscriptions = [];
    this.subscriptionsInProgress.forEach(async (subscription) => {
        try {
            await subscription._initiate();
        }
        catch (e) {
            this._log("Unable to resubscribe", e);
        }
    });
}, _Jolt_initializeWebSocket = function _Jolt_initializeWebSocket({ resolveConnection, rejectConnection, authToken, }) {
    this._log("Initiating WebSocket connection");
    this._webSocket = new WebSocket(`${this.wssEndpoint}?token=${authToken}`);
    this._webSocket.onerror = (event) => {
        this._log("WebSocket error:", event);
    };
    this._webSocket.onopen = () => {
        // Ensure the connection is kept alive by sending a ping message every configured interval
        this._ping = window.setInterval(() => {
            const ping = {
                event: "jolt.ping",
            };
            this._webSocket.send(JSON.stringify(ping));
        }, this._pingInterval);
    };
    this._webSocket.onclose = () => {
        this._log("disconnected");
        window.clearInterval(this._ping);
        this.subscriptions.forEach(({ channel }) => {
            this._handleMessage({ channel: channel, event: "jolt.unsubscribed" });
        });
        if (!this._intentionallyDisconnected) {
            const timeout = this._retryTimeout(null, 0);
            window.setTimeout(this._reconnectAndSubscribeUntilSuccessful.bind(this, resolveConnection, rejectConnection, timeout, 1), timeout);
            return;
        }
        this._intentionallyDisconnected = false;
        this.connection = null;
        this.subscriptions = [];
        this.subscriptionsInProgress = [];
    };
    this._webSocket.onmessage = (message) => {
        let wsData;
        try {
            wsData = JSON.parse(message.data);
        }
        catch (e) {
            // Safari sometiems receives extra empty messages
            // We should look into that, but for now just bail out.
            return;
        }
        if (wsData.event === "jolt.pong") {
            __classPrivateFieldGet(this, _Jolt_instances, "m", _Jolt_pong).call(this);
            return;
        }
        if (wsData.event === "jolt.connected") {
            const eventData = JSON.parse(wsData.data);
            this.connectionId = eventData.connectionId;
            resolveConnection(eventData);
            this._log("connected with ID:", eventData.connectionId);
            if (this._resubscribeOnReconnect) {
                __classPrivateFieldGet(this, _Jolt_instances, "m", _Jolt_resubscribeToAllSubscriptions).call(this);
            }
            return;
        }
        if (wsData.event === "jolt.error") {
            let maybeEventData = null;
            try {
                maybeEventData = JSON.parse(wsData.data);
            }
            catch (e) {
                // eventData will be null and the typecheck below will fail
            }
            if (typeof maybeEventData !== "object") {
                this._log("Unable to handle error event", wsData);
                return;
            }
            const message = maybeEventData.message;
            if (message.startsWith("Subscription failed:")) {
                this._failChannelSubscriptionInProgress(wsData.channel, message);
                return;
            }
            if (message.startsWith("Client sent message failed:")) {
                this._log(`Unable to publish message to ${wsData.channel}:`, message);
                return;
            }
            this._log("Unhandled error :(", wsData);
            return;
        }
        if (wsData.event === "jolt.subscribed") {
            this._succeedChannelSubscriptionInProgress(wsData.channel);
        }
        this._handleMessage(wsData);
        if (wsData.event === "jolt.unsubscribed") {
            this._removeSubscription(wsData.channel);
        }
    };
}, _Jolt_fetchAuthToken = async function _Jolt_fetchAuthToken(previousTimeout = null, retryCount = 0) {
    try {
        return await this.fetchAuthTokenFn();
    }
    catch (e) {
        this._log(e);
        const timeout = this._retryTimeout(previousTimeout, retryCount);
        this._log(`Could not fetch auth token. Retrying after ${timeout}ms...`);
        return new Promise((resolve) => {
            setTimeout(() => resolve(__classPrivateFieldGet(this, _Jolt_instances, "m", _Jolt_fetchAuthToken).call(this, timeout, retryCount + 1)), timeout);
        });
    }
};
export class ChannelSubscription {
    constructor(channel, client, fetchSubscribeTokenFn, claims = {}) {
        this.channel = channel;
        this.client = client;
        this.fetchSubscribeTokenFn = fetchSubscribeTokenFn;
        this.claims = claims;
        this.subscribed = false;
        this.bindings = [];
        this.enqueuedClientSentMessages = [];
    }
    bind(event, callback) {
        this.bindings.push({
            event,
            callback,
        });
        return () => this.unbind(event, callback);
    }
    unbind(event, callback) {
        this.bindings = this.bindings.filter((binding) => binding.event !== event || binding.callback !== callback);
    }
    async trigger(event, data) {
        if (typeof data !== "string") {
            throw new Error("message data must be supplied as a string");
        }
        await this.client.connection;
        const message = {
            channel: this.channel,
            event,
            data,
        };
        if (!this.subscribed) {
            this.client._log("Enqueueing client sent message for sending upon subscription success", message);
            this.enqueuedClientSentMessages.push({ event, data });
            return;
        }
        this.client._log("Triggering client sent message", message);
        return this.client._webSocket.send(JSON.stringify(message));
    }
    async _initiate(previousTimeout = null, retryCount = 0) {
        try {
            await this.client.connection;
        }
        catch (err) {
            this.client._failChannelSubscriptionInProgress(this.channel, "not connected");
            return;
        }
        if (typeof this.fetchSubscribeTokenFn !== "function" &&
            typeof this.client.fetchSubscribeTokenFn !== "function") {
            throw new Error("You must supply a fetchSubscribeTokenFn that will be called to authorize a channel");
        }
        let token;
        try {
            token = await this._fetchSubscribeToken();
        }
        catch (e) {
            this.client._log(e);
            const timeout = this.client._retryTimeout(previousTimeout, retryCount);
            this.client._log(`Could not fetch subscription token. Retrying in ${timeout}ms...`);
            window.setTimeout(this._initiate.bind(this, timeout, retryCount + 1), timeout);
            return;
        }
        const sub = {
            event: "jolt.subscribe",
            channel: this.channel,
            data: JSON.stringify({ token }),
        };
        this.client._webSocket.send(JSON.stringify(sub));
    }
    _unsubscribe() {
        const unsub = {
            event: "jolt.unsubscribe",
            channel: this.channel,
        };
        this.client._webSocket.send(JSON.stringify(unsub));
    }
    _onSubscribed() {
        this.subscribed = true;
        this.enqueuedClientSentMessages.forEach((message) => this.trigger(message.event, message.data));
        this.enqueuedClientSentMessages = [];
    }
    _onUnsubscribed() {
        this.subscribed = false;
    }
    async _fetchSubscribeToken() {
        if (typeof this.fetchSubscribeTokenFn === "function") {
            try {
                return await this.fetchSubscribeTokenFn(this.channel, this.client.connectionId, { claims: this.claims });
            }
            catch (err) {
                this.client._log("failed to subscribe to channel using local fetchSubscribeTokenFn", this.channel, err);
                throw err;
            }
        }
        try {
            return await this.client.fetchSubscribeTokenFn(this.channel, this.client.connectionId, { claims: this.claims });
        }
        catch (err) {
            this.client._log("failed to subscribe to channel using global fetchSubscribeTokenFn", this.channel, err);
            throw err;
        }
    }
}
//# sourceMappingURL=index.js.map